/**
 * News/NewsAdmin.jsx
 * ════════════════════════════════════════════════════════════════
 * Адмін-список новин. Спільний для порталу і садочка.
 *
 * Props:
 *   scope  {string}  — 'portal' | 'kg'
 *   kgId   {string?} — обов'язково для scope='kg'
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faSearch, faPencil, faTrash, faThumbtack,
    faEye, faEyeSlash, faBoxArchive, faRotateLeft,
    faImage, faChevronLeft, faChevronRight, faGear,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { constr_uri } from '../../../../../services/apiClient';
import { newsAPI, newsCategoriesAPI } from './newsAPI';
import NewsEditor from './NewsEditor';
import NewsCategoriesAdmin from './NewsCategoriesAdmin';

// ── Статуси ───────────────────────────────────────────────────────────────────

var STATUS_META = {
    draft:     { label: 'Чернетка',    color: '#6b7280', bg: '#f3f4f6' },
    published: { label: 'Опубліковано', color: '#059669', bg: '#ecfdf5' },
    hidden:    { label: 'Приховано',   color: '#d97706', bg: '#fffbeb' },
    archived:  { label: 'Архів',       color: '#9ca3af', bg: '#f9fafb' },
};

function formatDate(d) {
    if (!d) return '—';
    var dt = new Date(d);
    return dt.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + dt.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
}

// ── Рядок новини ──────────────────────────────────────────────────────────────

var NewsRow = function({ item, onEdit, onDelete, onStatusChange }) {
    var st = STATUS_META[item.status] || STATUS_META.draft;
    var [busy, setBusy] = useState(false);

    var coverSrc = item.coverDocId && item.coverFileId
        ? constr_uri('docfile?doc_id=' + item.coverDocId + '&file_id=' + item.coverFileId)
        : null;

    var isScheduled = item.status === 'published' && item.publishAt && new Date(item.publishAt) > new Date();
    var isExpired   = item.expireAt && new Date(item.expireAt) < new Date();

    var runAction = async function(fn) {
        setBusy(true);
        try { await fn(); } finally { setBusy(false); }
    };

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '12px 14px',
            border: '1px solid #e5e7eb', borderRadius: 10,
            background: '#fff', marginBottom: 8,
            opacity: busy ? 0.6 : 1,
            transition: 'opacity 0.15s',
        }}>
            {/* Обкладинка */}
            <div style={{
                width: 64, height: 64, borderRadius: 8, flexShrink: 0,
                background: '#f3f4f6', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {coverSrc
                    ? <img src={coverSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <FontAwesomeIcon icon={faImage} style={{ color: '#d1d5db', fontSize: 20 }} />}
            </div>

            {/* Інфо */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    {item.isPinned && <FontAwesomeIcon icon={faThumbtack} style={{ fontSize: 11, color: '#0069ff' }} title="Закріплено" />}
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                        fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                        background: st.bg, color: st.color,
                    }}>
                        {st.label}
                    </span>
                    {isScheduled && (
                        <span style={{ fontSize: 11, color: '#0069ff', fontWeight: 500 }}>
                            Заплановано на {formatDate(item.publishAt)}
                        </span>
                    )}
                    {isExpired && item.status === 'published' && !isScheduled && (
                        <span style={{ fontSize: 11, color: '#dc3545', fontWeight: 500 }}>
                            Період показу завершено
                        </span>
                    )}
                    {item.categoryName && (
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{item.categoryName}</span>
                    )}
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{formatDate(item.createdAt)}</span>
                </div>
            </div>

            {/* Дії */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {item.status === 'draft' && (
                    <ActionBtn title="Опублікувати" icon={faEye} color="#059669"
                        onClick={function() { runAction(function() { return onStatusChange(item._id, 'published'); }); }} />
                )}
                {item.status === 'published' && (
                    <ActionBtn title="Приховати" icon={faEyeSlash} color="#d97706"
                        onClick={function() { runAction(function() { return onStatusChange(item._id, 'hidden'); }); }} />
                )}
                {item.status === 'hidden' && (
                    <ActionBtn title="Показати знову" icon={faEye} color="#059669"
                        onClick={function() { runAction(function() { return onStatusChange(item._id, 'published'); }); }} />
                )}
                {(item.status === 'draft' || item.status === 'hidden' || item.status === 'published') && (
                    <ActionBtn title="Архівувати" icon={faBoxArchive} color="#9ca3af"
                        onClick={function() { runAction(function() { return onStatusChange(item._id, 'archived'); }); }} />
                )}
                {item.status === 'archived' && (
                    <ActionBtn title="Повернути в чернетки" icon={faRotateLeft} color="#0069ff"
                        onClick={function() { runAction(function() { return onStatusChange(item._id, 'draft'); }); }} />
                )}
                <ActionBtn title="Редагувати" icon={faPencil} color="#0069ff" onClick={function() { onEdit(item._id); }} />
                <ActionBtn title="Видалити" icon={faTrash} color="#dc3545"
                    onClick={function() {
                        if (window.confirm('Видалити новину "' + item.title + '"? Дію неможливо скасувати.')) {
                            runAction(function() { return onDelete(item._id); });
                        }
                    }} />
            </div>
        </div>
    );
};

function ActionBtn({ title, icon, color, onClick }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            style={{
                width: 30, height: 30, borderRadius: 7,
                border: '1px solid #e5e7eb', background: '#fff',
                color: color, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12,
            }}
        >
            <FontAwesomeIcon icon={icon} />
        </button>
    );
}

// ════════════════════════════════════════════════════════════════
// ГОЛОВНИЙ КОМПОНЕНТ
// ════════════════════════════════════════════════════════════════

var NewsAdmin = function({ scope, kgId }) {
    var [items, setItems]         = useState([]);
    var [total, setTotal]         = useState(0);
    var [page, setPage]           = useState(1);
    var [pages, setPages]         = useState(1);
    var [loading, setLoading]     = useState(true);

    var [search, setSearch]       = useState('');
    var [statusFilter, setStatusFilter] = useState('');
    var [categoryFilter, setCategoryFilter] = useState('');
    var [categories, setCategories] = useState([]);

    var [editingId, setEditingId] = useState(undefined); // undefined=закрито, null=створення, id=редагування
    var [showCategoriesAdmin, setShowCategoriesAdmin] = useState(false);

    var searchTimeout = useRef(null);
    var limit = 15;

    useEffect(function() {
        newsCategoriesAPI.list().then(setCategories).catch(function() {});
    }, []);

    var load = async function(p) {
        setLoading(true);
        try {
            var result = await newsAPI.getAdminList(scope, kgId, {
                status: statusFilter, category: categoryFilter, search: search,
                page: p || page, limit: limit,
            });
            setItems(result.items || []);
            setTotal(result.total || 0);
            setPages(result.pages || 1);
        } catch (e) {
            toast.error('Помилка завантаження новин');
        } finally {
            setLoading(false);
        }
    };

    useEffect(function() { load(1); setPage(1); }, [statusFilter, categoryFilter]);
    useEffect(function() { load(page); }, [page]);

    var handleSearch = function(e) {
        var val = e.target.value;
        setSearch(val);
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(function() { setPage(1); load(1); }, 350);
    };

    var handleStatusChange = async function(id, status) {
        try {
            await newsAPI.changeStatus(scope, kgId, id, status);
            toast.success('Статус оновлено');
            load(page);
        } catch (e) {
            toast.error('Помилка зміни статусу');
        }
    };

    var handleDelete = async function(id) {
        try {
            await newsAPI.remove(scope, kgId, id);
            toast.success('Новину видалено');
            load(page);
        } catch (e) {
            toast.error('Помилка видалення');
        }
    };

    var handleSaved = function() {
        setEditingId(undefined);
        load(page);
    };

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>

            {/* Панель керування */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                    <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#9ca3af' }} />
                    <input
                        type="text" value={search} onChange={handleSearch}
                        placeholder="Пошук за заголовком..."
                        style={{ width: '100%', height: 38, paddingLeft: 32, paddingRight: 12, border: '1px solid #ced4da', borderRadius: 8, fontSize: 13 }}
                    />
                </div>

                <select value={statusFilter} onChange={function(e) { setStatusFilter(e.target.value); }}
                    style={{ height: 38, borderRadius: 8, border: '1px solid #ced4da', fontSize: 13, padding: '0 10px' }}>
                    <option value="">Всі статуси</option>
                    {Object.keys(STATUS_META).map(function(s) { return <option key={s} value={s}>{STATUS_META[s].label}</option>; })}
                </select>

                <select value={categoryFilter} onChange={function(e) { setCategoryFilter(e.target.value); }}
                    style={{ height: 38, borderRadius: 8, border: '1px solid #ced4da', fontSize: 13, padding: '0 10px' }}>
                    <option value="">Всі категорії</option>
                    {categories.map(function(c) { return <option key={c._id} value={c._id}>{c.name}</option>; })}
                </select>

                {scope === 'portal' && (
                    <button type="button" onClick={function() { setShowCategoriesAdmin(true); }}
                        title="Керування категоріями"
                        style={{ height: 38, width: 38, borderRadius: 8, border: '1px solid #ced4da', background: '#fff', color: '#6b7280', cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faGear} />
                    </button>
                )}

                <button type="button" onClick={function() { setEditingId(null); }}
                    style={{
                        height: 38, padding: '0 16px', borderRadius: 8, border: 'none',
                        background: '#0069ff', color: '#fff', fontWeight: 600, fontSize: 13,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                    <FontAwesomeIcon icon={faPlus} /> Нова новина
                </button>
            </div>

            {/* Список */}
            {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 0', color: '#adb5bd' }}>
                    {search || statusFilter || categoryFilter ? 'Нічого не знайдено' : 'Немає новин. Натисніть "Нова новина" щоб створити першу.'}
                </div>
            ) : (
                <div>
                    {items.map(function(item) {
                        return (
                            <NewsRow
                                key={item._id}
                                item={item}
                                onEdit={setEditingId}
                                onDelete={handleDelete}
                                onStatusChange={handleStatusChange}
                            />
                        );
                    })}
                </div>
            )}

            {/* Пагінація */}
            {pages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
                    <button disabled={page <= 1} onClick={function() { setPage(function(p) { return p - 1; }); }}
                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.4 : 1 }}>
                        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 11 }} />
                    </button>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>{page} / {pages}</span>
                    <button disabled={page >= pages} onClick={function() { setPage(function(p) { return p + 1; }); }}
                        style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: page >= pages ? 'default' : 'pointer', opacity: page >= pages ? 0.4 : 1 }}>
                        <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 11 }} />
                    </button>
                </div>
            )}

            {/* Редактор */}
            {editingId !== undefined && (
                <NewsEditor
                    scope={scope}
                    kgId={kgId}
                    newsId={editingId}
                    onClose={function() { setEditingId(undefined); }}
                    onSaved={handleSaved}
                />
            )}

            {/* Керування категоріями (тільки portal) */}
            {showCategoriesAdmin && (
                <NewsCategoriesAdmin onClose={function() { setShowCategoriesAdmin(false); newsCategoriesAPI.list().then(setCategories); }} />
            )}
        </div>
    );
};

export default NewsAdmin;